import type { RefObject } from 'react'
import type {
	IClusterData,
	IMarkerData,
	MarkerFeature,
	MarkerGeometry,
	QueriedCluster,
	QueriedMarker,
} from './types'

const MIN_SIZE = 20
const minZero = (n: number) => Math.max(0, n)

export function resolveInsets(container: RefObject<HTMLElement | null>) {
	if (!container.current) return undefined
	const { offsetTop, offsetLeft, offsetWidth, offsetHeight } = container.current

	const insets = {
		left: minZero(offsetLeft),
		right: minZero(window.innerWidth - (offsetLeft + offsetWidth)),
		top: minZero(offsetTop),
		bottom: minZero(window.innerHeight - (offsetTop + offsetHeight)),
	}

	if (offsetWidth < MIN_SIZE) {
		const delta = (MIN_SIZE - offsetWidth) / 2
		insets.left = minZero(insets.left - delta)
		insets.right = minZero(insets.right - delta)
	}

	if (offsetHeight < MIN_SIZE) {
		const delta = (MIN_SIZE - offsetHeight) / 2
		insets.top = minZero(insets.top - delta)
		insets.bottom = minZero(insets.bottom - delta)
	}

	return insets
}

export function createFeatureCollection(features: MarkerFeature[] = []): MarkerGeometry {
	return { type: 'FeatureCollection', features }
}

/**
 * Converts a set of pins to a geometry feature collection.
 */
export function markersToGeometry(markers: IMarkerData[]): MarkerGeometry {
	const features = markers.map<MarkerFeature>((marker) => ({
		id: marker.id,
		type: 'Feature',
		properties: marker,
		geometry: {
			type: 'Point',
			coordinates: [marker.lng, marker.lat],
		},
	}))
	return createFeatureCollection(features)
}

export function isMarkerFeature(feature: GeoJSON.Feature): feature is QueriedMarker {
	return (
		!!feature.properties &&
		'id' in feature.properties &&
		'name' in feature.properties &&
		'lat' in feature.properties &&
		'lng' in feature.properties
	)
}

export function isClusterFeature(feature: GeoJSON.Feature): feature is QueriedCluster {
	return (
		!!feature.properties &&
		'cluster_id' in feature.properties &&
		'point_count' in feature.properties
	)
}

export function isMarkerData(data: IMarkerData | IClusterData): data is IMarkerData {
	return 'id' in data
}

export function isClusterData(data: IMarkerData | IClusterData): data is IMarkerData {
	return 'cluster_id' in data
}

export function getDataId(data: IMarkerData | IClusterData) {
	return isMarkerData(data) ? data.id : data.cluster_id
}

export function getFeatureId(feature: QueriedMarker | QueriedCluster) {
	return getDataId(feature.properties)
}
